import React, { useContext, useState, useEffect } from "react";
import _ from "lodash";
import { QueryContent } from "@repoflow/components/content-query";
import { useMutation } from "@apollo/client";
import * as Loading from "@repoflow/components/loading";
import { Provider as SchemaProvider } from "@repoflow/form-schema/schema-config";
import { useValueState } from "@repoflow/form-schema/section-state";
import * as NotifyUtils from "@repoflow/form-schema/notify";

export const Context = React.createContext(null);

export const useSectionContext = () => {
  return useContext(Context);
};

export const Button = ({
  enabledClass = "btn btn-success",
  disabledClass = "btn btn-secondary",
  onClick,
  children,
  type = "button",
  loading: inLoading,
  // For constraints the schema cannot express — a field that must equal
  // another one, say. Mirrors Action's prop of the same name.
  disabled,
}) => {
  const current = useContext(Context);

  const { state, loading } = current;
  const { isValid, isEqual } = state;

  const isLoading = loading || inLoading;
  const allowed = !isEqual() && isValid() && !isLoading && !disabled;

  return (
    <button
      type={type}
      className={allowed ? enabledClass : disabledClass}
      disabled={!allowed}
      onClick={(e) => {
        onClick ? onClick(current) : current.mutate();
      }}
    >
      {isLoading ? <Loading.Icon /> : children}
    </button>
  );
};

export const Provider = (props) => {
  const {
    children,

    schema,
    base,
    basepath,

    attributes,

    state,
    target,
  } = props;

  const current = useContext(Context);

  const sectionContext = {
    ...current,
    parent: current,
    schema: schema ? schema : current ? current.schema : null,
    children,
    base,
    basepath,
    attributes,
    state: state || (current ? current.state : null),
    target: target || (current ? current.target : null),
  };

  return (
    <SchemaProvider schema={schema} widgets={[]} config={null}>
      <Context.Provider value={sectionContext}>{children}</Context.Provider>
    </SchemaProvider>
  );
};

export const Query = ({ query, variables, datapath, children, context }) => {
  return (
    <QueryContent
      query={query}
      variables={variables}
      context={{ query, variables, datapath, children, context }}
    >
      {QueryHandler}
    </QueryContent>
  );
};

const QueryHandler = ({
  account,
  context,
  context: { children: Children },
  refetch,
}) => {
  const current = useContext(Context);
  const values = _.get(account, context.datapath);

  const sectionContext = {
    ...current,
    query: context,
    account,
    values,
  };

  return (
    <Context.Provider value={sectionContext}>
      <Children
        {...sectionContext}
        context={context.context}
        refetch={refetch}
      />
    </Context.Provider>
  );
};

export const Mutation = ({
  mutation,
  update,
  onMutate,
  onCompleted,
  onError,
  target,
  initial,
  children: Children,
  context,
  refetch = [],
}) => {
  const [trigger, triggerMutation] = useState(false);
  const current = useContext(Context);
  const initialVal = initial ? initial : current.values;

  const state = useValueState({
    target,
    initial: initialVal,
    context: current,
  });

  const sectionContext = {
    ...current,
    target,
    initial: initialVal,
    state,
    mutate: () => {
      triggerMutation(true);
    },
  };

  const [handler, { loading, error }] = useMutation(mutation, {
    refetchQueries: refetch,
    update,
    onCompleted: (data) => onCompleted && onCompleted(data, sectionContext),
    onError: (error) => {
      onError
        ? onError(error, sectionContext)
        : NotifyUtils.error(error.toString());
    },
  });

  sectionContext.handler = handler;
  sectionContext.loading = loading;
  sectionContext.error = error;

  useEffect(() => {
    if (trigger) {
      onMutate && onMutate(sectionContext);
      triggerMutation(false);
    }
  }, [trigger]);

  return (
    <Context.Provider value={sectionContext}>
      <Children {...sectionContext} context={context} />
    </Context.Provider>
  );
};

export const Action = ({
  disabled,
  refetch,
  update,
  mutation,
  enabledClass = "btn btn-success",
  disabledClass = "btn btn-secondary",
  onCompleted,
  onError,
  children,
  variables,
}) => {
  const current = useContext(Context);

  const { loading: busy } = current;

  const [handler, { loading, error }] = useMutation(mutation, {
    refetchQueries: refetch,
    update,
    onCompleted: (data) => onCompleted && onCompleted(data, current),
    onError: (error) => onError && onError(error, current),
  });

  const isLoading = loading;
  const allowed = !isLoading && !busy && !disabled;
  return (
    <button
      className={allowed ? enabledClass : disabledClass}
      disabled={!allowed}
      onClick={(e) => {
        handler({ variables });
      }}
    >
      {isLoading ? <Loading.Icon /> : children}
    </button>
  );
};
